import * as Sentry from '@sentry/nextjs';
import { getCaptchaHlBasedOnLocale } from '@tmh-website/shared/helpers';
import JSCookie from 'js-cookie';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import type { FormikConfig } from 'formik';
import { Field, Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@tmh-website/ui/Button';
import Checkbox from '@tmh-website/ui/Checkbox/Checkbox';
import FormSection from '@tmh-website/ui/FormSection';
import Input from '@tmh-website/ui/Input';
import Select from '@tmh-website/ui/Select';
import Textarea from '@tmh-website/ui/Textarea';
import theme from '@tmh-website/styles/theme';
import type { FormComponentBaseProps } from '@tmh-website/components/blocks/Form';
import type { SelectProps } from '@tmh-website/ui/Select';
import Yup from '@tmh-website/services/validation.service';
import { Breakpoint } from '@tmh-website/shared/const';
import { StaticFormVariant } from '@tmh-website/services/form.service';
import { StyledFormElements } from '@tmh-website/ui/FormElements';
import { SuccessMessage } from '@tmh-website/ui/FormResultMessages';

import * as StyledStaticForms from './StaticForms.styles';
import type { FieldName, W2CFieldLabels, W2CValues, Web2CaseStaticFormVariant } from './web2Case.service';
import {
  baseValidation,
  getDynamicFormConfig,
  getWeb2CasePayload,
  initialValues,
  validationIsRequired,
} from './web2Case.service';

interface ValuesWatcherProps {
  values: W2CValues;
  onChange: (values: W2CValues) => void;
}

const ValuesWatcher = ({ values, onChange }: ValuesWatcherProps) => {
  useEffect(() => {
    onChange(values);
  }, [values]);

  return null;
};

interface Web2CaseFormProps extends Omit<FormComponentBaseProps, 'formParameters'> {
  formVariant: Web2CaseStaticFormVariant;
}

const Web2CaseForm = ({
  formElementThemeColor,
  buttonThemeColor,
  buttonHoverThemeColor,
  formVariant,
}: Web2CaseFormProps) => {
  const { locale } = useRouter();
  const { i18n, t } = useTranslation();

  const [error, setError] = useState<string | null>(null);
  const [formConfig, setFormConfig] = useState(getDynamicFormConfig(initialValues, formVariant));
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const fieldLabels: W2CFieldLabels = useMemo(
    () => ({
      first_name: t('global:label.firstName'),
      last_name: t('global:label.lastName'),
      postal_code: t('global:label.zip'),
      street: t('global:label.streetAndHouseNumber'),
      city: t('global:label.city'),
      company: t('global:label.company'),
      phone: t('global:label.phone'),
      email: t('global:label.email'),
      customer_type: t('blocks:staticForms.customerTypeLabel'),
      issue_type: t('blocks:staticForms.issueTypeLabel'),
      manufacturer: t('global:label.manufacturer'),
      serial_number: t('global:label.serialNumber'),
      site_name: t('blocks:staticForms.siteName'),
      old_serial_number: t('global:label.oldSerialNumber'),
      new_serial_number: t('global:label.newSerialNumber'),
      station_online: t('blocks:staticForms.stationOnlineLabel'),
      wallbox_id: t('blocks:staticForms.wallboxIdLabel'),
      charging_issue_description: t('blocks:staticForms.chargingIssueDescriptionLabel'),
      charging_issue_error: t('blocks:staticForms.chargingIssueErrorLabel'),
      vehicle_name: t('blocks:staticForms.vehicleNameLabel'),
      rfid_card_id: t('blocks:staticForms.rfidCardIdLabel'),
      actions_description: t('blocks:staticForms.actionsDescriptionLabel'),
      customer_number: t('blocks:staticForms.customerNumber'),
      order_number: t('blocks:staticForms.orderNumber'),
      message: t('global:label.yourMessage'),
      submit: t('global:send'),
    }),
    []
  );

  /**
   * Resolves the options/values for the `<Select>` component that handles the `issue_type` field.
   */
  const resolveIssueTypeOptions = (): SelectProps['options'] => {
    let translationKey: string;

    switch (formVariant) {
      case StaticFormVariant.ETWeb2CaseContact: {
        translationKey = 'blocks:staticForms.issueTypeOptionsETContact';
        break;
      }
      case StaticFormVariant.ETWeb2CaseSupport: {
        translationKey = 'blocks:staticForms.issueTypeOptionsETSupport';
        break;
      }
      case StaticFormVariant.Web2Case:
      default: {
        translationKey = 'blocks:staticForms.issueTypeOptions';
        break;
      }
    }

    return t(translationKey, { returnObjects: true }) as SelectProps['options'];
  };

  /**
   * Formik's validation schema.
   */
  const validationSchema: FormikConfig<W2CValues>['validationSchema'] = () => {
    const dynamicValidation = {};

    Object.keys(formConfig)
      .filter(key => formConfig[key as FieldName].visible)
      .forEach(key => (dynamicValidation[key] = formConfig[key as FieldName].validation));

    return baseValidation.concat(Yup.object(dynamicValidation)).strict();
  };

  /**
   * Submits the form.
   *
   * ℹ️ The SalesForce endpoint for the Web2Case submissions returns the `200 OK` response even if the submission
   * was incorrectly structured, or hasn't been accepted for some reason. We're not really able to get a 100%
   * confirmation on whether the given form submission has been successfully saved in the SalesForce list of cases,
   * or not. Therefore, in case of any future changes to this form, please, remember to always check the SalesForce
   * panel if the form submissions are being correctly recorded.
   *
   * Google reCAPTCHA validation is always required, regardless of the environment that the form is being used on.
   * As of today (22.04.2024), we've got two SalesForce environments, and 2 pair of keys for the Google reCAPTCHA:
   *
   * - staging & localhost:
   *   - SalesForce env: sandbox
   *   - reCAPTCHA keys: staging
   * - production:
   *   - SalesForce env: production
   *   - reCAPTCHA keys: production
   */
  const submitForm = async (values: W2CValues) => {
    const payloadValues = getWeb2CasePayload(values, fieldLabels, { formVariant });

    try {
      await fetch(process.env['NEXT_PUBLIC_SALESFORCE_W2C_ENDPOINT'] as string, {
        body: new URLSearchParams(payloadValues as Record<string, any>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        method: 'POST',
        mode: 'no-cors',
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  /**
   * Determines whether the passed-in argument is an instance of the `Error` class
   * and has its associated error message.
   *
   * @param error - The subject to verify.
   *
   * @returns - `true` is the passed-in argument represents the Error class AND has non-empty message; `false` otherwise.
   */
  const hasErrorMessage = (error: unknown): error is Error => {
    return error instanceof Error && typeof error.message === 'string' && error.message.length > 0;
  };

  /**
   * Handles the `onSubmit` event fired by `<Formik>`.
   */
  const handleOnSubmit: FormikConfig<W2CValues>['onSubmit'] = async values => {
    setError(null);
    setIsLoading(true);

    try {
      await submitForm(values);
      setIsSent(true);
    } catch (error) {
      Sentry.captureException(error);

      if (hasErrorMessage(error)) {
        setError(error.message);
      } else {
        setError(t('global:errorUnknown'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isCurrentLocaleFr: boolean = i18n.language === 'fr';

  const renderFirstNameField = () => (
    <Field
      as={Input}
      themeColor={formElementThemeColor}
      label={fieldLabels.first_name}
      name='first_name'
      autoComplete='given-name'
      maxLength={40}
      required={validationIsRequired(formConfig.first_name.validation)}
    />
  );

  const renderLastNameField = () => (
    <Field
      as={Input}
      themeColor={formElementThemeColor}
      label={fieldLabels.last_name}
      name='last_name'
      autoComplete='family-name'
      maxLength={40}
      required={validationIsRequired(formConfig.last_name.validation)}
    />
  );

  return (
    <Formik<W2CValues> onSubmit={handleOnSubmit} {...{ initialValues, validationSchema }}>
      {({ setFieldValue, values, touched, errors, validateForm }) => {
        return (
          <Form data-cy='web2CaseForm'>
            <>
              <ValuesWatcher
                values={values}
                onChange={updatedValues => {
                  setFormConfig(getDynamicFormConfig(updatedValues, formVariant));
                  // Remove errors from empty optional fields
                  setTimeout(validateForm, 50);
                }}
              />
              <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
                {isCurrentLocaleFr ? (
                  <>
                    {renderLastNameField()}
                    {renderFirstNameField()}
                  </>
                ) : (
                  <>
                    {renderFirstNameField()}
                    {renderLastNameField()}
                  </>
                )}
                {formConfig.postal_code.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.postal_code}
                    name='postal_code'
                    autoComplete='zip-code'
                    maxLength={20}
                    required={validationIsRequired(formConfig.postal_code.validation)}
                  />
                )}

                {formConfig.street.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.street}
                    name='street'
                    autoComplete='street'
                    maxLength={80}
                    required={validationIsRequired(formConfig.street.validation)}
                  />
                )}
              </FormSection>

              {formConfig.city.visible && (
                <FormSection numberOfColumns={1}>
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.city}
                    name='city'
                    autoComplete='city'
                    maxLength={80}
                    required={validationIsRequired(formConfig.city.validation)}
                  />
                </FormSection>
              )}

              <FormSection numberOfColumns={formConfig.company.visible ? 2 : 1}>
                {formConfig.company.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.company}
                    name='company'
                    autoComplete='organization'
                    maxLength={80}
                    required={validationIsRequired(formConfig.company.validation)}
                  />
                )}

                <Field
                  as={Input}
                  themeColor={formElementThemeColor}
                  label={fieldLabels.phone}
                  name='phone'
                  autoComplete='phone'
                  type='tel'
                  maxLength={40}
                  required={validationIsRequired(formConfig.phone.validation)}
                />
              </FormSection>

              <FormSection numberOfColumns={1}>
                <Field
                  as={Input}
                  themeColor={formElementThemeColor}
                  label={fieldLabels.email}
                  name='email'
                  autoComplete='email'
                  type='email'
                  maxLength={80}
                  required={validationIsRequired(formConfig.email.validation)}
                />

                {formConfig.customer_type.visible && (
                  <Field
                    as={Select}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.customer_type}
                    name='customer_type'
                    options={t('blocks:staticForms.customerTypeOptions', { returnObjects: true })}
                    required={validationIsRequired(formConfig.customer_type.validation)}
                  />
                )}

                <Field
                  as={Select}
                  themeColor={formElementThemeColor}
                  label={fieldLabels.issue_type}
                  name='issue_type'
                  options={resolveIssueTypeOptions()}
                  required={validationIsRequired(formConfig.issue_type.validation)}
                />
              </FormSection>

              <FormSection numberOfColumns={1}>
                {formConfig.manufacturer.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.manufacturer}
                    name='manufacturer'
                    maxLength={80}
                    required={validationIsRequired(formConfig.manufacturer.validation)}
                  />
                )}

                {formConfig.serial_number.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.serial_number}
                    name='serial_number'
                    maxLength={80}
                    required={validationIsRequired(formConfig.serial_number.validation)}
                  />
                )}

                {formConfig.site_name.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.site_name}
                    name='site_name'
                    maxLength={80}
                    required={validationIsRequired(formConfig.site_name.validation)}
                  />
                )}

                {formConfig.old_serial_number.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.old_serial_number}
                    name='old_serial_number'
                    maxLength={80}
                    required={validationIsRequired(formConfig.old_serial_number.validation)}
                  />
                )}

                {formConfig.new_serial_number.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.new_serial_number}
                    name='new_serial_number'
                    maxLength={80}
                    required={validationIsRequired(formConfig.new_serial_number.validation)}
                  />
                )}

                {formConfig.station_online.visible && (
                  <Field
                    as={Select}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.station_online}
                    name='station_online'
                    options={[
                      { value: 'Yes', label: t('global:boolean.true') },
                      { value: 'No', label: t('global:boolean.false') },
                    ]}
                    required={validationIsRequired(formConfig.station_online.validation)}
                  />
                )}

                {formConfig.wallbox_id.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.wallbox_id}
                    name='wallbox_id'
                    maxLength={80}
                    required={validationIsRequired(formConfig.wallbox_id.validation)}
                  />
                )}

                {formConfig.charging_issue_description.visible && (
                  <Field
                    as={Textarea}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.charging_issue_description}
                    name='charging_issue_description'
                    rows='4'
                    maxLength={4000}
                    required={validationIsRequired(formConfig.charging_issue_description.validation)}
                  />
                )}

                {formConfig.charging_issue_error.visible && (
                  <Field
                    as={Textarea}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.charging_issue_error}
                    name='charging_issue_error'
                    rows='4'
                    maxLength={4000}
                    required={validationIsRequired(formConfig.charging_issue_error.validation)}
                  />
                )}

                {formConfig.vehicle_name.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.vehicle_name}
                    name='vehicle_name'
                    maxLength={80}
                    required={validationIsRequired(formConfig.vehicle_name.validation)}
                  />
                )}

                {formConfig.rfid_card_id.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.rfid_card_id}
                    name='rfid_card_id'
                    maxLength={80}
                    required={validationIsRequired(formConfig.rfid_card_id.validation)}
                  />
                )}

                {formConfig.actions_description.visible && (
                  <Field
                    as={Textarea}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.actions_description}
                    name='actions_description'
                    rows='4'
                    maxLength={4000}
                    required={validationIsRequired(formConfig.actions_description.validation)}
                  />
                )}

                {formConfig.customer_number.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.customer_number}
                    name='customer_number'
                    maxLength={40}
                    required={validationIsRequired(formConfig.customer_number.validation)}
                  />
                )}

                {formConfig.order_number.visible && (
                  <Field
                    as={Input}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.order_number}
                    name='order_number'
                    maxLength={40}
                    required={validationIsRequired(formConfig.order_number.validation)}
                  />
                )}

                {formConfig.message.visible && (
                  <Field
                    as={Textarea}
                    themeColor={formElementThemeColor}
                    label={fieldLabels.message}
                    name='message'
                    rows='4'
                    maxLength={8000}
                    required={validationIsRequired(formConfig.message.validation)}
                  />
                )}
              </FormSection>

              {/* Debug e-mail */}
              {(process.env.NODE_ENV === 'development' || JSCookie.get('tmh_debug') === 'true') && (
                <div style={{ padding: '16px', border: `1px solid ${theme.color.Red_500_Danger}` }}>
                  <p style={{ color: theme.color.Red_500_Danger }}>
                    <strong>ONLY VISIBLE IN DEVELOPMENT MODE</strong>
                  </p>
                  <FormSection numberOfColumns={1}>
                    <Field as={Checkbox} themeColor={formElementThemeColor} name='debug' type='checkbox'>
                      Enable debug mode which sends a debug email
                    </Field>
                  </FormSection>
                  {values.debug && (
                    <Field
                      as={Input}
                      themeColor={formElementThemeColor}
                      name='debugEmail'
                      label='debugEmail'
                      type='email'
                    />
                  )}
                  <p style={{ color: theme.color.Red_500_Danger }}>
                    <strong>This form needs to send the production key and therefore doesn't work on localhost.</strong>
                  </p>
                </div>
              )}
              {/* END | Debug e-mail */}

              {/* Google reCAPTCHA */}
              <StyledStaticForms.CaptchaContainer>
                <ReCAPTCHA
                  hl={getCaptchaHlBasedOnLocale(locale)}
                  onChange={token => setFieldValue('gRecaptchaResponse', token)}
                  sitekey={
                    process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? process.env.NX_NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ''
                  }
                  size='normal'
                />

                {touched.gRecaptchaResponse && errors.gRecaptchaResponse && (
                  <StyledFormElements.ErrorMessage>{t('global:errorMissingCaptcha')}</StyledFormElements.ErrorMessage>
                )}
              </StyledStaticForms.CaptchaContainer>
              {/* END | Google reCAPTCHA */}

              {/* "Submit" button */}
              <Button
                disabled={isSent || Object.keys(touched).length === 0 || Object.keys(errors).length > 0}
                hoverThemeColor={buttonHoverThemeColor}
                isLoading={isLoading}
                minWidth
                themeColor={buttonThemeColor}
                type='submit'>
                {t('global:send')}
              </Button>
              {/* END | "Submit" button */}

              {/* Success message/alert/banner */}
              {isSent && <SuccessMessage marginTop>{t('global:contact.thanksMessage')}</SuccessMessage>}

              {/* Error message/alert/banner */}
              {error && <StyledFormElements.ErrorMessage>{error}</StyledFormElements.ErrorMessage>}
            </>
          </Form>
        );
      }}
    </Formik>
  );
};
export default Web2CaseForm;
